import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Tabs, TabsProps } from 'antd';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { language2currencyMap, RegionFixedExchangeRateValue } from 'services/config/region';
import DataUpdate from '../DataUpdate';
import CurrencySelector from './components/Filter/CurrencySelector';
import OrderOverview from './components/OrderOverview';
import OrderWastage from './components/OrderWastage';
import Review from './components/Review';
import SaleConvert from './components/SaleConvert';
import { DashboardCurrencyCtx } from './hooks/useCurrency';
import { useGetPermissionScopes } from 'hooks/business/useNewPermisson';
import AppleDetail from '../Apple/Detail';
import { useAuthorization } from 'components/Authorization';

const StickyContainer = styled.div`
  position: sticky;
  top: -16px;
  z-index: 999;
`;
const StyledTabs = styled(Tabs)`
  overflow: visible;
  .ant-tabs-nav {
    background-color: var(--fett-bg-panel);
    margin-bottom: 0;
    padding: 0 16px;
    border-radius: 4px 4px 0 0;
  }
  .ant-tabs-tab {
    color: var(--fett-text-secondary);
  }
  .ant-tabs-tab:hover {
    color: var(--fett-text-primary);
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--fett-app-primary-color);
  }
  .ant-tabs-ink-bar {
    background: var(--fett-app-primary-color);
  }
  .ant-tabs-extra .ant-select .ant-select-selector {
    background-color: var(--fett-bg-input) !important;
    border-color: var(--fett-border-default) !important;
  }
  .ant-tabs-extra .ant-select-selection-item {
    color: var(--fett-text-secondary);
  }
  .ant-tabs-extra .ant-select-arrow {
    color: var(--fett-text-tertiary);
  }
  > div > .site-custom-tab-bar {
    margin-top: 16px;
    margin-bottom: 0;
  }
  .ant-tabs-tabpane {
    overflow: visible;
  }
`;
const tabName = {
  ORDER_OVERVIEW: 'orderOverview',
  SALES_CONVERT: 'salesConvert',
  ORDER_WASTAGE: 'orderWastage',
  REVIEW: 'review',
  APPLE_ORDER_DETAIL: 'appleOrderDetail',
  APPLE_CONVERSATION_DETAIL: 'appleConversationDetail',
} as const;
type TabNameType = (typeof tabName)[keyof typeof tabName];

const Deal = () => {
  const [currency, setCurrency] = useState<RegionFixedExchangeRateValue>();
  const { t } = useTranslate();
  const [tab, setTab] = useState<TabNameType>('orderOverview');
  const extraNode = useMemo(() => {
    const content =
      tab === 'orderWastage' ? (
        <p
          className={css`
            white-space: pre-wrap;
            line-height: 1.3;
            margin-bottom: 0;
          `}
        >
          {t('ViewDashboard.nav.数据损耗数据更新说明')}
        </p>
      ) : null;

    return {
      right: (
        <DataUpdate content={content}>
          <CurrencySelector onCurrencyChange={setCurrency} currency={currency} />
        </DataUpdate>
      ),
    };
  }, [currency, t, tab]);

  const ctx = useMemo(() => {
    return {
      currency,
      onChange: setCurrency,
    };
  }, [currency, setCurrency]);

  const renderTabBar: TabsProps['renderTabBar'] = (props, DefaultTabBar) => (
    <StickyContainer>
      <DefaultTabBar {...props} className="site-custom-tab-bar" />
    </StickyContainer>
  );
  const lang = useSystemLanguageValue();
  useEffect(() => {
    const next = language2currencyMap[lang];
    setCurrency(next);
  }, [lang]);

  const saleConvertPermission = useGetPermissionScopes()('履约数据')?.some(
    ({ platform, storeCount }) => platform !== 'MERCADO' && storeCount > 0,
  );

  const { payload } = useAuthorization<Oversea.Token.Payload>();

  return (
    <DashboardCurrencyCtx.Provider value={ctx}>
      <StyledTabs
        tabBarExtraContent={extraNode}
        renderTabBar={renderTabBar}
        animated
        destroyInactiveTabPane
        onChange={useCallback((key: string) => {
          setTab(key as TabNameType);
        }, [])}
      >
        <Tabs.TabPane key={tabName.ORDER_OVERVIEW} tab={t('ViewDashboard.履约数据.tab.订单概况')}>
          <OrderOverview />
        </Tabs.TabPane>
        {saleConvertPermission && (
          <Tabs.TabPane key={tabName.SALES_CONVERT} tab={t('ViewDashboard.履约数据.tab.销售转化')}>
            <SaleConvert />
          </Tabs.TabPane>
        )}
        <Tabs.TabPane key={tabName.ORDER_WASTAGE} tab={t('ViewDashboard.履约数据.tab.订单损耗')}>
          <OrderWastage />
        </Tabs.TabPane>
        <Tabs.TabPane key={tabName.REVIEW} tab={t('ViewDashboard.履约数据.tab.评价数据')}>
          <Review />
        </Tabs.TabPane>
        {payload?.orgId === 50189 && [
          <Tabs.TabPane
            key={tabName.APPLE_ORDER_DETAIL}
            tab={lang === 'zh-CN' ? '订单明细' : 'Order Details'}
          >
            <AppleDetail type="ORDER" />
          </Tabs.TabPane>,
          <Tabs.TabPane
            key={tabName.APPLE_CONVERSATION_DETAIL}
            tab={lang === 'zh-CN' ? '会话明细' : 'Contact Details with Survey and Assisted Order'}
          >
            <AppleDetail type="CONVERSATION" />
          </Tabs.TabPane>,
        ]}
      </StyledTabs>
    </DashboardCurrencyCtx.Provider>
  );
};

export default Deal;
